import { useState } from "react";
import {
  X,
  Building2,
  FileText,
  Loader2,
  Tag,
  CalendarDays,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { apiFetch } from "@/lib/api";
import CompanyPartnerSection from "./CompanyPartnerSection";

function formatCNPJ(value) {
  const digits = value.replace(/\D/g, "");
  return digits
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1/$2")
    .replace(/(\d{4})(\d)/, "$1-$2")
    .substring(0, 18);
}

/**
 * Todos os valores de ClientAccount.status (espelha ACCOUNT_STATUSES em
 * packages/shared/account/accountStatus.js). Um status fora desta lista deixava
 * o Select vazio — o Radix não tem item para exibir.
 */
const ACCOUNT_STATUS_OPTIONS = [
  { value: "pending_verification", label: "Aguardando verificação", dot: "bg-sky-400" },
  { value: "active", label: "Ativo", dot: "bg-emerald-400" },
  { value: "trial", label: "Trial", dot: "bg-yellow-400" },
  { value: "trial_expired", label: "Trial expirado", dot: "bg-zinc-400" },
  { value: "suspended", label: "Suspenso", dot: "bg-orange-400" },
  { value: "churned", label: "Cancelado", dot: "bg-red-400" },
];

function buildInitialFormData(editingCompany, defaultPlanType) {
  if (!editingCompany) {
    return {
      companyName: "",
      cnpj: "",
      planType: defaultPlanType,
      status: "active",
      billingCycle: "monthly",
      commercialDiscountType: "percent",
      commercialDiscountValue: "",
      commercialDiscountExpiresAt: "",
      commercialDiscountDescription: "",
    };
  }
  return {
    companyName: editingCompany.companyName,
    cnpj: formatCNPJ(editingCompany.cnpj),
    // `planType` é a coluna legada e pode guardar um valor que não é slug de
    // nenhum plano ("starter", "entry"...) enquanto `plan` aponta o plano real.
    // O dropdown usa o slug como value, então a relação vence.
    planType: editingCompany.plan?.slug || editingCompany.planType,
    status: editingCompany.status || "active",
    billingCycle: editingCompany.billingCycle || "monthly",
    commercialDiscountType: editingCompany.commercialDiscountType || "percent",
    commercialDiscountValue: editingCompany.commercialDiscountValue ?? "",
    commercialDiscountExpiresAt: editingCompany.commercialDiscountExpiresAt
      ? new Date(editingCompany.commercialDiscountExpiresAt)
          .toISOString()
          .slice(0, 10)
      : "",
    commercialDiscountDescription:
      editingCompany.commercialDiscountDescription || "",
    updateAsaas: false,
    isPartner: !!editingCompany.isPartner,
  };
}

/**
 * CompanyFormModal — "Nova Empresa" / "Editar Empresa" (superadmin, /dashboard).
 *
 * Extraído de Dashboard.jsx: o modal tinha crescido a ponto de o cabeçalho
 * ficar atrás da barra de favoritos e os botões atrás da barra de status do
 * navegador. O shell agora é limitado por `100dvh` (não `100vh` — é o que
 * desconta a barra do navegador) com cabeçalho e rodapé fixos e só o corpo
 * rolando.
 */
export default function CompanyFormModal({
  editingCompany,
  availablePlans,
  onClose,
  onSaved,
}) {
  const defaultPlanType = availablePlans[0]?.id || "";
  const [formData, setFormData] = useState(() =>
    buildInitialFormData(editingCompany, defaultPlanType),
  );
  const [formError, setFormError] = useState("");

  // Valores gravados que não existem nas opções ganham um item próprio, para o
  // campo nunca aparecer vazio (e o Save não trocar o valor sem o usuário ver).
  const planOptions =
    formData.planType &&
    !availablePlans.some((plan) => plan.id === formData.planType)
      ? [...availablePlans, { id: formData.planType, name: formData.planType }]
      : availablePlans;
  const statusOptions = ACCOUNT_STATUS_OPTIONS.some(
    (option) => option.value === formData.status,
  )
    ? ACCOUNT_STATUS_OPTIONS
    : [
        ...ACCOUNT_STATUS_OPTIONS,
        { value: formData.status, label: formData.status, dot: "bg-zinc-400" },
      ];
  const [isSubmitting, setIsSubmitting] = useState(false);

  // O valor de isPartner como o servidor o conhecia quando o modal abriu —
  // nunca muda durante a vida deste componente. Os controles de token exigem
  // os dois lados (isPartner E savedIsPartner) para habilitar: ligar o switch
  // agora não basta, o flag só existe de fato no banco depois do Save.
  const [savedIsPartner] = useState(!!editingCompany?.isPartner);

  // Valida o desconto comercial contra o valor mensal do plano (ajustado pelo ciclo).
  // Retorna mensagem de erro ou null. Espelha a validação do backend.
  function getDiscountValidationError() {
    if (!editingCompany) return null;
    const { commercialDiscountType: type, commercialDiscountValue: raw } =
      formData;
    if (raw === "" || raw == null) return null;
    const value = Number(raw);
    if (Number.isNaN(value)) return null;
    if (type === "percent") {
      return value < 0 || value > 100
        ? "Percentual deve ser entre 0 e 100."
        : null;
    }
    // fixo: não pode exceder o valor mensal do plano
    const monthly = Number(editingCompany.plan?.monthlyPrice || 0);
    const yearly = editingCompany.plan?.yearlyPrice
      ? Number(editingCompany.plan.yearlyPrice) / 12
      : null;
    const planMonthly =
      formData.billingCycle === "anual" && yearly != null ? yearly : monthly;
    return value > planMonthly
      ? `Desconto não pode ser maior que o valor do plano (R$ ${planMonthly.toFixed(2).replace(".", ",")}).`
      : null;
  }
  const discountValidationError = getDiscountValidationError();

  async function handleSubmit(e) {
    e.preventDefault();
    if (discountValidationError) {
      setFormError(discountValidationError);
      return;
    }
    setIsSubmitting(true);
    setFormError("");

    try {
      const isEditing = !!editingCompany;

      const res = await apiFetch(
        isEditing
          ? `/api/internal/companies/${editingCompany.id}`
          : "/api/internal/companies",
        {
          method: isEditing ? "PUT" : "POST",
          body: JSON.stringify(formData),
        },
      );

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || "Erro ao salvar empresa.");
      }

      onSaved?.();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm animate-in fade-in duration-200 p-4">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-lg max-h-[calc(100dvh-2rem)] flex flex-col bg-surface border border-border-strong rounded-2xl shadow-2xl animate-in zoom-in-95 duration-300 overflow-hidden"
      >
        <header className="shrink-0 flex items-center justify-between px-6 py-4 border-b border-border">
          <h3 className="text-xl font-bold text-foreground-strong">
            {editingCompany ? "Editar Empresa" : "Nova Empresa"}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="p-1 text-foreground-muted hover:text-foreground transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </header>

        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain custom-scrollbar px-6 py-4 space-y-5">
          {formError && (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-700 dark:text-red-400 text-sm">
              {formError}
            </div>
          )}

          {/* Dados */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground-secondary mb-2">
                Nome da Empresa
              </label>
              <div className="relative">
                <Building2 className="absolute left-3 top-3.5 w-5 h-5 text-foreground-muted" />
                <input
                  type="text"
                  value={formData.companyName}
                  onChange={(e) =>
                    setFormData({ ...formData, companyName: e.target.value })
                  }
                  placeholder="Ex: Empresa ABC Ltda"
                  className="w-full pl-10 pr-4 py-3 bg-background border border-border rounded-xl text-foreground-strong placeholder:text-placeholder focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-all"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground-secondary mb-2">
                  CNPJ
                </label>
                <div className="relative">
                  <FileText className="absolute left-3 top-3.5 w-5 h-5 text-foreground-muted" />
                  <input
                    type="text"
                    value={formData.cnpj}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        cnpj: formatCNPJ(e.target.value),
                      })
                    }
                    placeholder="00.000.000/0000-00"
                    className="w-full pl-10 pr-4 py-3 bg-background border border-border rounded-xl text-foreground-strong placeholder:text-placeholder focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-all"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground-secondary mb-2">
                  Tipo de Plano
                </label>
                <Select
                  value={formData.planType}
                  onValueChange={(value) =>
                    setFormData({ ...formData, planType: value })
                  }
                >
                  <SelectTrigger className="w-full bg-background border-border text-foreground-strong">
                    <SelectValue placeholder="Selecione o plano" />
                  </SelectTrigger>
                  <SelectContent>
                    {/* Só o slug: há vários planos com o mesmo nome ("Entry",
                        "Plus"), e é o slug que distingue um do outro. */}
                    {planOptions.map((plan) => (
                      <SelectItem key={plan.id} value={plan.id} className="font-mono">
                        {plan.id}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Plano & cobrança (edit only) */}
          {editingCompany && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground-secondary mb-2">
                  Status da Conta
                </label>
                <Select
                  value={formData.status}
                  onValueChange={(value) =>
                    setFormData({ ...formData, status: value })
                  }
                >
                  <SelectTrigger className="w-full bg-background border-border text-foreground-strong">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    {statusOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        <span className="flex items-center gap-2">
                          <span className={`w-2 h-2 rounded-full ${option.dot}`} />
                          {option.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="flex items-center gap-1.5 text-sm font-medium text-foreground-secondary mb-2">
                  <CalendarDays className="w-4 h-4" />
                  Ciclo de cobrança
                </label>
                <div className="flex gap-2">
                  {[
                    { value: "monthly", label: "Mensal" },
                    { value: "anual", label: "Anual" },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() =>
                        setFormData({ ...formData, billingCycle: opt.value })
                      }
                      className={`flex-1 py-2 text-sm rounded-xl border transition-colors ${
                        formData.billingCycle === opt.value
                          ? "border-cyan-500 bg-cyan-500/15 text-cyan-700 dark:text-cyan-300"
                          : "border-border text-foreground-muted hover:border-border-strong"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Desconto comercial (edit only) */}
          {editingCompany && (
            <div>
              <label className="flex items-center gap-1.5 text-sm font-medium text-foreground-secondary mb-2">
                <Tag className="w-4 h-4" />
                Desconto comercial (opcional)
              </label>

              {/* Discount type toggle */}
              <div className="flex gap-2 mb-2">
                {[
                  { value: "percent", label: "%" },
                  { value: "fixed", label: "R$" },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() =>
                      setFormData({
                        ...formData,
                        commercialDiscountType: opt.value,
                        commercialDiscountValue: "",
                      })
                    }
                    className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${
                      formData.commercialDiscountType === opt.value
                        ? "border-amber-500 bg-amber-500/15 text-amber-700 dark:text-amber-300"
                        : "border-border text-foreground-muted hover:border-border-strong"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              <div className="flex gap-2">
                <div className="flex-1">
                  <label className="text-xs text-foreground-muted mb-1 block">
                    {formData.commercialDiscountType === "percent"
                      ? "Desconto (%)"
                      : "Desconto (R$)"}
                  </label>
                  <input
                    type="number"
                    min="0"
                    max={
                      formData.commercialDiscountType === "percent"
                        ? 100
                        : undefined
                    }
                    step="0.01"
                    value={formData.commercialDiscountValue}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        commercialDiscountValue: e.target.value,
                      })
                    }
                    placeholder={
                      formData.commercialDiscountType === "percent"
                        ? "Ex.: 15"
                        : "Ex.: 100"
                    }
                    className="w-full px-3 py-2 bg-background border border-border rounded-xl text-sm text-foreground-strong placeholder:text-placeholder focus:outline-none focus:border-cyan-500 transition-all"
                  />
                </div>
                <div className="flex-1">
                  <label className="text-xs text-foreground-muted mb-1 flex items-center gap-1">
                    <CalendarDays className="w-3 h-3" />
                    Válido até (opcional)
                  </label>
                  <input
                    type="date"
                    value={formData.commercialDiscountExpiresAt}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        commercialDiscountExpiresAt: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 bg-background border border-border rounded-xl text-sm text-foreground-strong focus:outline-none focus:border-cyan-500 transition-all"
                    title="Válido até (opcional)"
                  />
                </div>
              </div>

              {/* Discount description */}
              <div className="mt-2">
                <label className="text-xs text-foreground-muted mb-1 block">
                  Descrição do desconto (opcional)
                </label>
                <input
                  type="text"
                  maxLength={120}
                  value={formData.commercialDiscountDescription}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      commercialDiscountDescription: e.target.value,
                    })
                  }
                  placeholder="Ex.: Parceria comercial 2026"
                  className="w-full px-3 py-2 bg-background border border-border rounded-xl text-sm text-foreground-strong placeholder:text-placeholder focus:outline-none focus:border-cyan-500 transition-all"
                />
              </div>

              {formData.commercialDiscountValue > 0 && (
                <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">
                  Desconto de{" "}
                  {formData.commercialDiscountType === "percent"
                    ? `${formData.commercialDiscountValue}%`
                    : `R$ ${Number(formData.commercialDiscountValue).toFixed(2).replace(".", ",")}`}
                  {formData.commercialDiscountExpiresAt &&
                    ` até ${new Date(formData.commercialDiscountExpiresAt).toLocaleDateString("pt-BR")}`}
                </p>
              )}

              {discountValidationError && (
                <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                  {discountValidationError}
                </p>
              )}
            </div>
          )}

          {/* Update Asaas checkbox (edit only) */}
          {editingCompany && (
            <label className="flex items-start gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={!!formData.updateAsaas}
                onChange={(e) =>
                  setFormData({ ...formData, updateAsaas: e.target.checked })
                }
                className="mt-0.5 h-4 w-4 rounded border-border text-cyan-600 focus:ring-cyan-500"
              />
              <span className="text-sm text-foreground-secondary">
                Atualizar valor no Asaas
                <span className="block text-xs text-foreground-muted">
                  Recalcula plano + addons − descontos e atualiza a assinatura
                  mensal no Asaas.
                </span>
              </span>
            </label>
          )}

          {/* Parceria (edit only) */}
          {editingCompany && (
            <CompanyPartnerSection
              accountId={editingCompany.id}
              isPartner={!!formData.isPartner}
              savedIsPartner={savedIsPartner}
              onIsPartnerChange={(next) =>
                setFormData((fd) => ({ ...fd, isPartner: next }))
              }
            />
          )}
        </div>

        <footer className="shrink-0 flex gap-3 px-6 py-4 border-t border-border bg-surface">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-3 bg-surface-active hover:bg-border-strong text-foreground-secondary font-medium rounded-xl transition-all"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={isSubmitting || !!discountValidationError}
            className="flex-1 py-3 bg-cyan-600 hover:bg-cyan-500 text-white font-semibold rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : editingCompany ? (
              "Salvar"
            ) : (
              "Criar Empresa"
            )}
          </button>
        </footer>
      </form>
    </div>
  );
}
